import React from 'react';
import {
  Action,
  ConfirmModal,
  FormHandlers,
  Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { ShootingStatus } from '../../../domain/types';
import { Shooting } from '../../../providers/api/dtos';
import { updateShooting } from '../../../providers/api/shooting/shootingProvider';
import { useComputedShootingStatus } from '../../hooks/use-computed-shooting-status/useComputedShootingStatus';
import UpsertShootingForm, { UpsertShootingFormModel } from '../../components/upsert-shooting-form/UpsertShootingForm';
import { shootingToUpsertShootingFormModel, upsertShootingFormModelToShooting } from '../../utils';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import usePhotographer from '../../hooks/use-photographer/usePhotographer';
import { upsertShootingValidationSchema } from '../../components/upsert-shooting-form/UpsertShootingForm.schema';

interface ChildFnProps {
  isSaving: boolean,
  cancel: VoidFunction,
}

export interface CancelShootingActionProps extends React.AriaAttributes {
  shooting: Shooting,
  onSuccess?: (cancelledShooting: Shooting) => void,
  children?: (props: ChildFnProps) => React.ReactNode
}

export const CANCEL_SHOOTING_ERROR_MESSAGE = 'Non è stato possibile annullare lo shooting.';
export const CANCEL_SHOOTING_SUCCESS_MESSAGE = 'Lo shooting è stato annullato con successo.';

const CancelShootingAction: React.FC<CancelShootingActionProps> = ({
  shooting,
  onSuccess,
  children,
  ...rest
}) => {
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const [isSaving, setIsSaving] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState<1 | 2>(1);

  const { userIsContentEditor, userIsAdmin } = useRBAC();
  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const { data } = useComputedShootingStatus(shooting);
  const { data: photographer } = usePhotographer(shooting.photographerId);

  const canBeCancelled = data?.canBeUpdatedTo(ShootingStatus.CANCELLED);

  const closeModal = React.useCallback(() => {
    modal.close();
    setCurrentStep(1);
  }, [modal]);

  const confirmCancel = React.useCallback(async (formValues: UpsertShootingFormModel) => {
    setIsSaving(true);

    try {
      const shootingToCancel: Shooting = upsertShootingFormModelToShooting(formValues, {
        ...shooting,
        projectStatus: ShootingStatus.CANCELLED,
      });

      const cancelledShooting = await updateShooting(shootingToCancel, shooting);

      setIsSaving(false);
      closeModal();

      addSuccess(CANCEL_SHOOTING_SUCCESS_MESSAGE);
      onSuccess?.(cancelledShooting);
    } catch (error) {
      addError(CANCEL_SHOOTING_ERROR_MESSAGE);
      setIsSaving(false);
    }
  }, [shooting, closeModal, addSuccess, onSuccess, addError]);

  const handleConfirm = React.useCallback(() => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else {
      formRef.current?.handleSubmit();
    }
  }, [currentStep]);

  if (!canBeCancelled) {
    return null;
  }

  return (
    <>
      {children
        ? children({
          isSaving,
          cancel: modal.open,
        })
        : (
          <Action
            label="Annulla shooting"
            aria-label="Pulsante per annullare lo shooting"
            onClick={() => modal.open()}
            loading={isSaving}
            {...rest}
          />
        )}
      <Portal>
        <ConfirmModal
          isOpen={modal.isOpen}
          title="Conferma annullamento shooting"
          aria-label="Conferma annullamento shooting"
          onConfirm={handleConfirm}
          variant="critical"
          onAbort={closeModal}
          loading={isSaving}
        >
          {currentStep === 1 && "Confermando l'operazione lo shooting sarà annullato."}
          {currentStep === 2 && (
            <UpsertShootingForm
              initialValues={shootingToUpsertShootingFormModel(shooting, photographer)}
              innerRef={formRef}
              onSubmit={confirmCancel}
              loading={isSaving}
              validationSchema={upsertShootingValidationSchema(shooting)}
              hiddenFields={(!userIsContentEditor && !userIsAdmin) ? {
                virtualTourType: true,
                deliveryDate: true,
                photoPrice: true,
                expensesRefund: true,
                extraServicesPrice: true,
              } : undefined}
              disabledFields={{
                type: true,
                photographer: true,
                date: true,
                time: true,
                virtualTourType: true,
                deliveryDate: true,
                photoPrice: true,
                expensesRefund: true,
                extraServicesPrice: true,
              }}
              size="S"
            />
          )}
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default CancelShootingAction;
